import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import {
  getCompanyId,
  getPurchaseOrdersPendingApproval,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  getPurchaseOrderLines,
  getLocations,
} from "../services/bcApi";

export function useBCData() {
  const { instance, accounts } = useMsal();
  const [orders, setOrders] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [locationsById, setLocationsById] = useState({});

  const getToken = useCallback(async () => {
    const request = { ...loginRequest, account: accounts[0] };
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  }, [instance, accounts]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const cId = companyId || (await getCompanyId(token));
      if (!companyId) setCompanyId(cId);
      const data = await getPurchaseOrdersPendingApproval(token, cId);
      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, companyId]);

  useEffect(() => {
    if (accounts.length > 0) loadOrders();
  }, [accounts, loadOrders]);

  // Fetch warehouses once so the line detail view can resolve location GUIDs
  // to readable codes. Failures are non-fatal — we just won't show codes.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getToken()
      .then((t) => getLocations(t, companyId))
      .then((locs) => {
        if (cancelled) return;
        const map = {};
        for (const l of locs) map[l.id] = l;
        setLocationsById(map);
      })
      .catch(() => {
        /* ignore — UI falls back to "Asignado" */
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, getToken]);

  const fetchLines = useCallback(
    async (orderId) => {
      const token = await getToken();
      return getPurchaseOrderLines(token, companyId, orderId);
    },
    [getToken, companyId]
  );

  const approve = useCallback(
    async (orderId) => {
      setActionLoading(orderId);
      try {
        const token = await getToken();
        await approvePurchaseOrder(token, companyId, orderId);
        await loadOrders();
        return { success: true };
      } catch (err) {
        return { success: false, error: err.message };
      } finally {
        setActionLoading(null);
      }
    },
    [getToken, companyId, loadOrders]
  );

  const reject = useCallback(
    async (orderId) => {
      setActionLoading(orderId);
      try {
        const token = await getToken();
        await rejectPurchaseOrder(token, companyId, orderId);
        await loadOrders();
        return { success: true };
      } catch (err) {
        return { success: false, error: err.message };
      } finally {
        setActionLoading(null);
      }
    },
    [getToken, companyId, loadOrders]
  );

  // Bulk variants for multi-select mode. We loop sequentially (BC custom API
  // pages don't love parallel writes on the same table) and reload only once
  // at the end. Returns per-order results so the UI can surface partial
  // failures.
  const bulkAction = useCallback(
    async (orderNumbers, kind) => {
      const fn = kind === "approve" ? approvePurchaseOrder : rejectPurchaseOrder;
      const results = [];
      let token;
      try {
        token = await getToken();
      } catch (err) {
        return orderNumbers.map((n) => ({ orderNumber: n, success: false, error: err.message }));
      }
      for (const num of orderNumbers) {
        try {
          await fn(token, companyId, num);
          results.push({ orderNumber: num, success: true });
        } catch (err) {
          results.push({ orderNumber: num, success: false, error: err.message });
        }
      }
      await loadOrders();
      return results;
    },
    [getToken, companyId, loadOrders]
  );

  const approveBulk = useCallback((nums) => bulkAction(nums, "approve"), [bulkAction]);
  const rejectBulk = useCallback((nums) => bulkAction(nums, "reject"), [bulkAction]);

  return {
    orders,
    loading,
    error,
    actionLoading,
    approve,
    reject,
    approveBulk,
    rejectBulk,
    fetchLines,
    reload: loadOrders,
    locationsById,
  };
}
